import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';

import { PasswordRules, evaluatePassword } from '../../utils/passwordPolicy';
import { PasswordRuleRow, PasswordRuleState } from './PasswordRuleRow';

const keyPrefix = 'components.form-outputs.PasswordRulesChecklist';

type Props = {
  /**
   * Con `true`, las reglas sin cumplir pasan de gris a rojo. Lo activa el
   * formulario cuando ya hubo un intento de envío: antes de eso, una regla que
   * el vendedor todavía no alcanza a escribir no es un error.
   */
  showErrors?: boolean;
  value: string;
};

/**
 * Muestra las tres reglas de la política mientras el vendedor escribe, para que
 * sepa qué le falta antes de enviar y no lo descubra por un mensaje de error.
 *
 * A propósito no es una región en vivo. Anunciar las tres reglas en cada tecla
 * convierte el escribir una contraseña en una interrupción constante; el estado
 * queda disponible para quien navegue la lista, que es el patrón habitual.
 */
export const PasswordRulesChecklist: FC<Props> = ({
  showErrors = false,
  value,
}): JSX.Element => {
  const { t } = useTranslation('translation', { keyPrefix });
  const rules = evaluatePassword(value);

  const items: { id: keyof PasswordRules; met: boolean }[] = [
    { id: 'length', met: rules.length },
    { id: 'uppercase', met: rules.uppercase },
    { id: 'special', met: rules.special },
  ];

  return (
    <Stack
      component='ul'
      spacing={0.5}
      sx={{ listStyle: 'none', m: 0, p: 0 }}
    >
      {items.map(({ id, met }) => {
        const state: PasswordRuleState = met
          ? 'met'
          : showErrors
          ? 'failed'
          : 'pending';

        return (
          <PasswordRuleRow key={id} state={state}>
            {t(id)}
          </PasswordRuleRow>
        );
      })}
    </Stack>
  );
};
